import { cn } from '@/lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  /** Optional single line under the spinner. Keep it short, or omit it. */
  text?: string;
  className?: string;
  /** Fill the viewport instead of a 200px block. */
  fullScreen?: boolean;
}

const SPINNER_SIZES = {
  sm: 'h-5 w-5 border-2',
  md: 'h-8 w-8 border-2',
  lg: 'h-11 w-11 border-[3px]',
} as const;

/**
 * The app's single loading affordance: a spinner, and at most one line of text.
 *
 * It used to render a bordered card full of skeleton blocks plus a paragraph of
 * copy, which read as a page that had finished loading badly rather than one
 * still loading , and it made every waiting state feel heavier than the screen
 * it was standing in for. Route-level skeletons that mirror a specific layout
 * still belong in that route's `loading.tsx`; this is the generic case.
 */
export function LoadingSpinner({ size = 'md', text, className, fullScreen = false }: LoadingSpinnerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        fullScreen ? 'min-h-screen w-full' : 'min-h-[200px] w-full',
        className,
      )}
    >
      <span
        className={cn(
          'animate-spin rounded-full border-[var(--dm-card-border)] border-t-primary motion-reduce:animate-none',
          SPINNER_SIZES[size],
        )}
      />
      {text ? (
        <p className="max-w-xs text-center text-sm text-[var(--color-text-secondary)]">{text}</p>
      ) : null}
      <span className="sr-only">{text || 'Loading'}</span>
    </div>
  );
}

export default LoadingSpinner;
